<!--
  SendWhitelistManage.vue
  테스트 환경 발송 화이트리스트 관리 페이지

  주요 기능:
  - 발송 허용 번호 추가 / 삭제
  - 여기 없는 번호는 intest/stg 환경의 CRM 알림톡·브랜드메시지·SMS 발송이 차단된다
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- 페이지 헤더 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">발송 화이트리스트 관리</h1>
          </div>
          <span class="pageDesc">
            테스트 환경(intest / stg)에서 CRM 알림톡·브랜드메시지·SMS 를 받을 수
            있는 번호를 등록합니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 -->
    <div class="px-0 pb-2">
      <div class="card content-wrap-card p-3">
        <a-alert
          type="warning"
          show-icon
          class="mb-3"
          message="여기 등록되지 않은 번호로는 테스트 환경에서 메시지가 발송되지 않습니다"
          description="운영(prod) 환경은 이 목록의 영향을 받지 않고 평소대로 발송됩니다. 목록이 비어 있으면 테스트 환경의 모든 발송이 차단됩니다. 이메일 발송은 대상이 아닙니다."
        />

        <!-- 번호 추가 -->
        <div class="add-row">
          <a-input
            v-model:value="newPhone"
            placeholder="발송을 허용할 번호를 입력하세요 (예: 010-1234-5678)"
            :maxlength="20"
            style="max-width: 320px"
            :disabled="loading"
            @pressEnter="handleAdd"
          />
          <a-button
            type="primary"
            class="yellow-button"
            :loading="saving"
            :disabled="loading"
            @click="handleAdd"
          >
            추가
          </a-button>
        </div>

        <!-- 번호 목록 -->
        <a-table
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          :pagination="false"
          row-key="phone"
          size="middle"
          class="mt-3"
          :locale="{
            emptyText:
              '등록된 번호가 없습니다. 지금 상태에서는 테스트 환경 발송이 전부 차단됩니다.',
          }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'action'">
              <a-popconfirm
                title="이 번호를 화이트리스트에서 삭제하시겠습니까?"
                ok-text="삭제"
                cancel-text="취소"
                @confirm="handleDelete(record.phone)"
              >
                <a-button
                  danger
                  size="small"
                  :loading="deletingPhone === record.phone"
                >
                  삭제
                </a-button>
              </a-popconfirm>
            </template>
          </template>
        </a-table>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { message } from 'ant-design-vue';
import SendWhitelistApi from './api';

/** 하이픈/공백을 제거해 숫자만 남긴다. 서버도 동일하게 정규화한다. */
const normalizePhone = (raw) => (raw ?? '').replace(/\D/g, '');

/** 010-1234-5678 형태로 보기 좋게 표시한다. 규격 밖이면 원문 그대로. */
const formatPhone = (digits) => {
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};

export default {
  name: 'SendWhitelistManage',
  setup() {
    const columns = [
      {
        title: '번호',
        dataIndex: 'display',
        key: 'display',
      },
      {
        title: '관리',
        key: 'action',
        width: 100,
      },
    ];

    const phones = ref([]);
    const newPhone = ref('');
    const loading = ref(false);
    const saving = ref(false);
    const deletingPhone = ref(null);

    const rows = computed(() =>
      phones.value.map((phone) => ({ phone, display: formatPhone(phone) })),
    );

    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      const msg =
        error.response?.data?.message || error.message || defaultMessage;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg);
    };

    const load = async () => {
      loading.value = true;
      try {
        const response = await SendWhitelistApi.get();
        phones.value = Array.isArray(response.data?.phones)
          ? response.data.phones
          : [];
      } catch (error) {
        handleError(error, '화이트리스트를 불러오지 못했습니다.');
        phones.value = [];
      } finally {
        loading.value = false;
      }
    };

    /** 목록 전체를 서버에 반영한다. 실패 시 서버 상태로 되돌린다. */
    const save = async (nextPhones, successMessage) => {
      saving.value = true;
      try {
        const response = await SendWhitelistApi.replace(nextPhones);
        phones.value = Array.isArray(response.data?.phones)
          ? response.data.phones
          : nextPhones;
        message.success(successMessage);
      } catch (error) {
        handleError(error, '화이트리스트 저장에 실패했습니다.');
        await load();
      } finally {
        saving.value = false;
      }
    };

    const handleAdd = async () => {
      const digits = normalizePhone(newPhone.value);
      if (!digits) {
        message.warning('번호를 입력해주세요.');
        return;
      }
      if (digits.length < 9 || digits.length > 11) {
        message.warning('번호 자릿수를 확인해주세요. (9~11자리)');
        return;
      }
      if (phones.value.includes(digits)) {
        message.warning('이미 등록된 번호입니다.');
        return;
      }

      await save([...phones.value, digits], '번호가 등록되었습니다.');
      newPhone.value = '';
    };

    const handleDelete = async (phone) => {
      deletingPhone.value = phone;
      try {
        await save(
          phones.value.filter((p) => p !== phone),
          '번호가 삭제되었습니다.',
        );
      } finally {
        deletingPhone.value = null;
      }
    };

    onMounted(load);

    return {
      columns,
      rows,
      newPhone,
      loading,
      saving,
      deletingPhone,
      handleAdd,
      handleDelete,
    };
  },
};
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin: 0;
}

.pageDesc {
  color: #67748e;
  font-size: 0.9rem;
}

.add-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
